import { Box, Button, Typography, CircularProgress, IconButton } from "@mui/material";
import React, { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import VisibilityIcon from "@mui/icons-material/Visibility";

const DocumentUploader = ({ uploadedFiles = [], setUploadedFiles = () => {}, maxFiles = 5 }) => {
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showToast, setShowToast] = useState(null);
  const fileInputRef = useRef();
  const { t } = useTranslation();

  const handleFileUpload = async (e) => {
    const selectedFiles = Array.from(e.target.files);

    if (uploadedFiles?.length + selectedFiles.length > maxFiles) {
      setShowToast({ error: true, label: t("CS_MAXIMUM_FILES_EXCEEDED", { maxFiles }) });
      return;
    }

    selectedFiles.forEach(async (file) => {
      const allowedFileTypesRegex = /(.*?)(jpg|jpeg|png|image|pdf)$/i;
      if (file.size >= 5242880) {
        setShowToast({ error: true, label: t("CS_MAXIMUM_UPLOAD_SIZE_EXCEEDED") });
      } else if (file?.type && !allowedFileTypesRegex.test(file?.type)) {
        setShowToast({ error: true, label: t("NOT_SUPPORTED_FILE_TYPE") });
      } else {
        setIsLoading(true);
        try {
          const response = await Digit.UploadServices.Filestorage("OBPS", file, Digit.ULBService.getStateId() || tenantId?.split(".")[0]);
          console.log("File upload response:", response);
          if (response?.data?.files?.length > 0) {
            setUploadedFiles((prev) => {
              const prevFiles = Array.isArray(prev) ? prev : [];
              return [...prevFiles, { fileStoreId: response?.data?.files[0]?.fileStoreId, fileName: file.name }];
            });
          } else {
            setShowToast({ error: true, label: t("1 error") });
          }
        } catch (err) {
          console.error("File upload error:", err);
          setShowToast({ error: true, label: t("CS_FILE_UPLOAD_ERROR") });
        } finally {
          setIsLoading(false);
        }
      }
    });
  };

  const deleteFile = (fileStoreId) => {
    setUploadedFiles((prev) => prev.filter((file) => file.fileStoreId !== fileStoreId));
  };

  return (
    <React.Fragment>
      <Box display="grid" gridTemplateColumns="repeat(12, 1fr)" gap={2}>
        <Box gridColumn="span 2" sx={{ fontWeight: "bold", marginTop: "5px", marginLeft: "5px", whiteSpace: "nowrap", fontSize: "14px" }}>
          Document/Certificate
        </Box>
        <Box gridColumn="span 10" sx={{ marginLeft: "20px" }}>
          <Button
            variant="outlined"
            component="span"
            onClick={() => fileInputRef.current.click()}
            sx={{
              width: "100%",
              mb: 1,
              display: "flex",
              justifyContent: "space-between",
              height: "30px",
              backgroundColor: "rgb(21, 124, 179)",
              color: "white",
              "&:hover": {
                backgroundColor: "#004e6c",
                color: "#fff",
              },
            }}
            disabled={uploadedFiles?.length >= maxFiles}
          >
            {isLoading ? (
              <Box sx={{ justifyContent: "center", position: "absolute", alignItems: "center", display: "flex", inset: 0 }}>
                <CircularProgress sx={{ color: "#ffffff" }} size={28} />
              </Box>
            ) : (
              <React.Fragment>
                <CloudUploadOutlinedIcon />
                <span style={{ position: "absolute", fontSize: "0.5rem", marginLeft: "8em" }}>{t("Attach document")}</span>
              </React.Fragment>
            )}
          </Button>
          <input ref={fileInputRef} type="file" style={{ display: "none" }} onChange={handleFileUpload} multiple />
          <Box mt={2}>
            {Array.isArray(uploadedFiles) &&
              uploadedFiles?.map((file, index) => (
                <Box
                  key={index}
                  display="flex"
                  alignItems="center"
                  mb={1}
                  sx={{ backgroundColor: "#eee", padding: "0.5rem", borderRadius: "0.25rem" }}
                >
                  <Typography variant="body2" sx={{ flexGrow: 1 }}>
                    {file.fileName}
                  </Typography>
                  <IconButton size="small" color="info" sx={{ fontSize: "1rem" }}>
                    <VisibilityIcon />
                  </IconButton>
                  <IconButton size="small" color="error" sx={{ fontSize: "1rem" }} onClick={() => deleteFile(file.fileStoreId)}>
                    <DeleteForeverIcon />
                  </IconButton>
                </Box>
              ))}
          </Box>
        </Box>
      </Box>
      {!!showToast?.error && <span className="errorText">{showToast.label}</span>}
    </React.Fragment>
  );
};

export default DocumentUploader;
